const { concat, cloneDeep, filter, find, isEqual, map, remove, uniqWith } = require('lodash');
const { withPrefix } = require('gatsby-link');
const { getImage } = require('gatsby-plugin-image');
const he = require('he');

const imageProperties = {
  'alternative-en': 'alternativeTextEN',
  'alternative-fr': 'alternativeText',
  'caption-en': 'captionEN',
  'caption-fr': 'caption',
  'creator-en': 'creator',
  'creator-fr': 'creator',
  'legend-label-en': 'Cover image caption: ',
  'legend-label-fr': "Légende de l'image de couverture : ",
  'legend-label-credits-en': 'credits: ',
  'legend-label-credits-fr': 'crédits : ',
};

const altNN = (alt) => {
  return alt || '';
};

const getImageAndAlt = (obj, websiteLanguage) => {
  if (obj?.imageFile) {
    const ret = {
      image: getImage(obj.imageFile),
      alt: obj[imageProperties[`alternative-${websiteLanguage}`]],
    };
    const lr = [];
    const captionProp = imageProperties[`caption-${websiteLanguage}`];
    if (obj[captionProp]) {
      const legendLabel = imageProperties[`legend-label-${websiteLanguage}`];
      lr.push(`${legendLabel} ${obj[captionProp]}`);
    }
    const creatorProp = imageProperties[`creator-${websiteLanguage}`];
    if (obj[creatorProp]) {
      const creatorLabel = imageProperties[`legend-label-credits-${websiteLanguage}`];
      lr.push(`(${creatorLabel} ${obj[creatorProp]})`);
    }
    if (lr.length > 0) {
      ret.legend = lr.join(' ');
    }
    return ret;
  }
  return null;
};

const sortAffiliations = (items) => {
  if (!items || items.length === 0) return [];
  const withoutDates = items.filter((item) => item.start_date === null && item.end_date === null);
  const withoutEndDate = items.filter((item) => item.end_date === null && item.start_date !== null);
  const withEndDate = items.filter((item) => item.end_date !== null);

  withoutEndDate.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  withEndDate.sort((a, b) => {
    const startA = new Date(a.start_date);
    const startB = new Date(b.start_date);
    const endA = new Date(a.end_date);
    const endB = new Date(b.end_date);

    if (startA < startB) return 1;
    if (startA > startB) return -1;

    const durationA = endA - startA;
    const durationB = endB - startB;
    return durationB - durationA;
  });

  return [...withoutEndDate, ...withEndDate, ...withoutDates];
};

const getLatestOrOngoingPosition = (array) => {
  if (!array || array.length === 0) {
    return null;
  }
  array.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  const lastaffiliation = array.find((position) => position.end_date === null);
  if (lastaffiliation) {
    return lastaffiliation;
  }
  return array[0];
};

const getLogoAndAlt = (obj, websiteLanguage) => {
  if (obj?.imageFile) {
    const prop = websiteLanguage === 'en' ? 'alternativeTextEN' : 'alternativeText';
    return {
      url: obj.imageFile.publicURL || obj.imageFile.gatsbyImageData?.images?.fallback?.src,
      alt: obj[prop],
    };
  }
  return null;
};

const toJSON = (value) => {
  if (value) {
    if (typeof value === 'string') {
      return JSON.parse(value);
    }
    return JSON.parse(JSON.stringify(value));
  }
  return null;
};

const extractHTMLText = (html) => {
  let cpyhtml = html;
  if (cpyhtml) {
    cpyhtml = cpyhtml.replace(/<figure(.)*[^>]>(.*)<\/figure>/gi, '');
    cpyhtml = cpyhtml.replace(/(<([^>]+)>)/gi, '');

    if (cpyhtml.length > 350) {
      cpyhtml = cpyhtml.substring(0, 350);
      const lastIndexOfSpace = cpyhtml.lastIndexOf(' ');
      if (lastIndexOfSpace > 0) {
        cpyhtml = cpyhtml.substring(0, lastIndexOfSpace);
      }
    }
  }

  return cpyhtml;
};

const getFirstContentDescription = (content) => {
  if (!content) return null;

  if (Array.isArray(content)) {
    for (const item of content) {
      const description = getFirstContentDescription(item);
      if (description) return description;
    }
    return null;
  }

  const { __typename } = content;

  if (__typename === 'CmsPortalComponentTabTab') {
    for (const tab of content.TabPanel || []) {
      const description = extractHTMLText(tab.content);
      if (description) return description;
    }
  }

  if (__typename === 'CmsPortalComponentContentText') {
    return extractHTMLText(content.text);
  }

  return null;
};

const getMetaDescription = (page, seo) => {
  if (seo?.Meta) {
    for (const s of seo.Meta) {
      if (s.name === 'description' && s.content) {
        return s.content;
      }
    }
  }
  if (page) {
    if (page.heading && page.heading.trim() !== '') {
      return page.heading.trim();
    }

    const encDesscription = getFirstContentDescription(page.content);
    if (encDesscription) {
      return he.decode(encDesscription);
    }
  }
  return null;
};

const getPageSeo = (item) => {
  if (item) {
    if (item.page) {
      // internal page
      const page = item.page;
      return {
        title: page.title,
        locale: page.locale || page.evt_language || null,
        seo: page.seo,
        link: page.canonical_link,
      };
    } else if (item.researcher) {
      const researcher = item.researcher;
      return {
        title: `${researcher.firstname} ${researcher.lastname}`,
        locale: null,
        link: null,
        seo: researcher.seo,
      };
    } else if (item.cmsPortalEventGuide) {
      const guide = item.cmsPortalEventGuide;
      return {
        title: guide.title,
        locale: guide.locale || null,
        seo: guide.seo,
        link: guide.canonical_link,
      };
    } else if (item.cmsPortalNewsGuide) {
      const guide = item.cmsPortalNewsGuide;
      return {
        title: guide.title,
        locale: guide.locale || null,
        seo: guide.seo,
        link: guide.canonical_link,
      };
    }
  }
  return null;
};

const getWebsiteSeo = (website) => {
  if (website) {
    return {
      name: website.name,
      locale: website.locale,
      seo: website.seo,
      description: website.description,
    };
  }
  return null;
};

const getGeneralSeo = (settings) => {
  if (settings) {
    return {
      seo: settings.seo,
    };
  }
  return null;
};

const getWebsiteLogo = (website, gs, language) => {
  if (website?.logo) {
    return getLogoAndAlt(website.logo, language);
  } else if (gs?.logo) {
    return getLogoAndAlt(gs.logo, language);
  }
  return null;
};

const getWebsiteFooter = (website, gs, siteURL, requiresAuth) => {
  const footer = (requiresAuth && website?.footer_private) || website?.website_footer || gs?.footer || null;
  if (footer) {
    const { logo, ...otherProperties } = footer;
    if (logo) {
      return {
        ...otherProperties,
        logo: {
          ...getLogoAndAlt(logo, website?.locale),
          navigationPath: siteURL,
        },
      };
    }
  }
  return footer;
};

const getPartnerLogo = (website) => {
  if (website?.partner_logo?.logo) {
    return {
      linkTo: website.partner_logo.URL,
      ...getLogoAndAlt(website.partner_logo.logo, website.locale),
    };
  }
  return null;
};

const getCanonicalLink = (data) => {
  if (data?.page?.canonical_link) return data?.page?.canonical_link;
  if (data?.cmsPortalNewsGuide?.canonical_link) return data?.cmsPortalNewsGuide?.canonical_link;
  if (data?.cmsPortalEventGuide?.canonical_link) return data?.cmsPortalEventGuide?.canonical_link;
  return null;
};

const getLayoutInformations = (data, siteURL, requiresAuth) => {
  if (!data) {
    throw new Error('invalid arguments. The data object is required');
  }
  const locale = data.website?.locale || 'fr';
  const generalSettings = data[`${locale}GeneralSettings`];
  return {
    locale: data?.website?.locale || null,
    localizations: data?.website?.localizations || null,
    name: data?.website?.name || null,
    logo: {
      ...getWebsiteLogo(data?.website, generalSettings, locale),
      navigationPath: siteURL,
    },
    logoPartner: getPartnerLogo(data?.website),
    favicon: data?.website?.favicon || null,
    menu: toJSON(data?.website?.menu)?.menu || [],
    corporateMenu: toJSON(data?.website?.corporate_menu?.menu) || [],
    communityMenu: toJSON(data?.website?.community_menu?.menu),
    footer: getWebsiteFooter(data?.website, generalSettings, siteURL, requiresAuth),
    generalSeo: getGeneralSeo(generalSettings),
    websiteSeo: getWebsiteSeo(data?.website),
    pageSeo: getPageSeo(data),
    canonical_link: getCanonicalLink(data),
    metaDescription: getMetaDescription(data?.page, data?.page?.seo),
  };
};

const getSEOInformations = (data) => {
  if (!data) {
    throw new Error('invalid arguments. The data object is required');
  }
  return {
    pageSeo: getPageSeo(data),
    canonical_link: getCanonicalLink(data),
    metaDescription: getMetaDescription(data?.page, data?.page?.seo),
  };
};

const mapSectionIntoMain = (content) => {
  return content?.map((elem) => {
    if (elem?.__typename === 'CmsPortalComponentSectionSectionContainer') {
      if (elem.section?.content?.length > 0) {
        elem = elem.section?.content[0];
      }
      elem.__typename = 'CmsPortalComponentSectionMain';
    }
    return elem;
  });
};

//aucune idee de pourquoi on a besoin de cette fonction
const removeUselessWidgets = (content) => {
  content?.forEach((item) => {
    if (item.__typename === 'CmsPortalComponentSectionMain') {
      remove(item?.content, (item) => {
        if (item.widget?.content?.length > 0) {
          return (
            item.widget?.content[0]?.__typename === 'CmsPortalComponentWidgetTwitter'
            // || item.widget?.content[0]?.__typename === 'CmsPortalComponentWidgetYoutubeStream'
            //|| item.widget?.content[0]?.__typename === 'CmsPortalComponentWidgetEmbededContent'
          );
        }
        return false;
      });
    }
  });
  return content;
};

const getInternalPageVideoHeader = (header) => {
  if (header && header.length > 0) {
    const hd = header[0];
    if (hd && hd.__typename === 'CmsPortalComponentContentPageVideoHeader') {
      return hd;
    }
  }
  return null;
};

const getInternalPageCover = (header) => {
  if (header && header.length > 0) {
    const hd = header[0];
    if (hd) {
      return hd.cover_media || null;
    }
  }
  return null;
};

const getInternalPageCarrousel = (header) => {
  if (header && header.length > 0) {
    const hd = header[0];
    if (hd?.carousel_item?.length > 0) {
      return hd;
    }
  }
  return null;
};

const parseYoutubeTimecode = (url) => {
  if (!url) return null;
  const match = url.match(/[?&]t=([^&]+)/);
  if (!match) return null;
  const raw = match[1];
  let seconds = 0;
  const hMatch = raw.match(/(\d+)h/);
  const mMatch = raw.match(/(\d+)m/);
  const sMatch = raw.match(/(\d+)s?$/);
  if (hMatch) seconds += parseInt(hMatch[1]) * 3600;
  if (mMatch) seconds += parseInt(mMatch[1]) * 60;
  if (sMatch) seconds += parseInt(sMatch[1]);
  return seconds || null;
};

const getDayFirstLocale = (locale) => {
  if (!locale) return 'fr';
  return locale.startsWith('en') ? 'en-GB' : locale;
};

const getLongDate = (date, locale) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString(getDayFirstLocale(locale), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'CET',
  });
};

const getDate = (date, delimiter, options, locale) => {
  let parsedDate = date;
  if (!delimiter) {
    delimiter = '.';
  }
  const standardOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'CET',
  };
  if (date) {
    parsedDate =
      new Date(date).toLocaleString(locale || 'fr-FR', {
        day: options?.day ? options.day : standardOptions.day,
        timeZone: 'CET',
      }) +
      delimiter +
      new Date(date).toLocaleString(locale || 'fr-FR', {
        month: options?.month ? options.month : standardOptions.month,
        timeZone: 'CET',
      }) +
      delimiter +
      new Date(date).toLocaleString(locale || 'fr-FR', {
        year: options?.year ? options.year : standardOptions.year,
        timeZone: 'CET',
      });
  }
  return parsedDate;
};

const getHour = (date) => {
  let parsedDate = date;
  if (date) {
    parsedDate = new Date(date).toLocaleTimeString('fr-FR', {
      hour: 'numeric',
      minute: 'numeric',
      timeZone: 'CET',
    });
  }
  return parsedDate;
};

const getHeader = (promotedEvents, headerItems, siteURL, limit, news) => {
  limit = limit || 20;
  let counter = 0;
  const filterInfo = (toFilter, counter, news) => {
    const res = [];
    toFilter?.forEach((item) => {
      if (counter < limit) {
        const obj = {
          newWindow: true,
          title: item.title,
          heading: item.heading,
          id: item.id,
          media: item.cover,
        };
        if (news) {
          obj.slug = `${siteURL}/news/${item.slug}`;
          obj.news_item = item;
        } else {
          obj.slug = `${siteURL}/events/${item.slug}`;
          obj.event = item;
        }
        res.push(obj);
        counter++;
      }
    });
    return res;
  };
  const filteredPromotedEvents = filterInfo(promotedEvents, counter, news);
  counter = counter + filteredPromotedEvents.length;
  const filteredHeaderEvents = filterInfo(headerItems, counter, news);
  return uniqWith(concat(filteredPromotedEvents, filteredHeaderEvents), isEqual);
};

const getFilteredGuide = (list, othList) => {
  const arr = cloneDeep(list);
  remove(arr, (item) => {
    if (find(othList, (headerItem) => item.id === headerItem.id)) return true;
  });
  return arr;
};

const getSiteURL = (url) => {
  if (url && !['/', ''].includes(url) && !url.endsWith('/')) {
    return `${url}/`;
  }
  return url;
};

const handleLanguageSelection = (siteURL, language) => {
  if (!language) {
    return '';
  }
  siteURL = withPrefix(siteURL);

  if (siteURL.endsWith('/en') || siteURL.endsWith('/fr')) {
    return siteURL.slice(0, -2) + language.locale + '/';
  }
  if (siteURL.endsWith('/en/') || siteURL.endsWith('/fr/')) {
    return siteURL.slice(0, -3) + language.locale + '/';
  }
  if (siteURL.endsWith('/')) {
    return `${siteURL}${language.locale}/`;
  }
  return `${siteURL}/${language.locale}/`;
};

const displayLanguages = (lang) => {
  let display;

  switch (lang?.locale) {
    case 'fr':
      display = 'Français';
      break;
    case 'en':
      display = 'English';
      break;
    default:
      display = lang;
  }

  return display;
};

const displayAriaLabelLanguages = (lang) => {
  let display;

  switch (lang?.locale) {
    case 'fr':
      display = 'Version française du site internet';
      break;
    case 'en':
      display = 'English version of the website';
      break;
    default:
      display = lang;
  }

  return display;
};

const getFilteredAlgoliaResults = (hits) => {
  const filtered = [];
  if (hits?.length > 0) {
    filtered.push({
      type: 'internalpage',
      hits: filter(hits, (item) => item.type === 'internalpage'),
    });
    filtered.push({
      type: 'news',
      hits: filter(hits, (item) => item.type === 'news'),
    });
    filtered.push({
      type: 'event',
      hits: filter(hits, (item) => item.type === 'event'),
    });
    filtered.push({
      type: 'exedproduct',
      hits: filter(hits, (item) => item.type === 'exedproduct'),
    });
    filtered.push({
      type: 'people',
      hits: filter(hits, (item) => item.type === 'people'),
    });
  }
  return filtered;
};

const getDisplayLang = (itemLanguage, locale) => {
  let displayLang = '';
  if (locale && itemLanguage) {
    if (itemLanguage.toLowerCase() !== locale.toLowerCase()) {
      displayLang = itemLanguage.toLowerCase();
    }
  }
  return displayLang;
};

module.exports = {
  altNN,
  getImageAndAlt,
  sortAffiliations,
  getLatestOrOngoingPosition,
  getLogoAndAlt,
  toJSON,
  getMetaDescription,
  getLayoutInformations,
  getSEOInformations,
  mapSectionIntoMain,
  removeUselessWidgets,
  getInternalPageVideoHeader,
  getInternalPageCover,
  getInternalPageCarrousel,
  getDayFirstLocale,
  getLongDate,
  getDate,
  getHour,
  getHeader,
  getFilteredGuide,
  getSiteURL,
  handleLanguageSelection,
  displayLanguages,
  displayAriaLabelLanguages,
  getFilteredAlgoliaResults,
  getDisplayLang,
  parseYoutubeTimecode,
};
